import { useEffect, useMemo, useState } from 'react';
import WalletCard from './WalletCard';
import PlayerCard from './PlayerCard';
import PhotoLightbox from './PhotoLightbox';
import FilterCard from './FilterCard';
import type { Player, Team, AppUser, GenderFilter, SideFilter } from '../types';
import { Download, Star, Search, RotateCcw, Users, ArrowRight, ArrowLeft, ArrowLeftRight } from 'lucide-react';
import Pagination from './Pagination';

interface OwnerDashboardProps {
  currentUser: AppUser;
  teams: Team[];
  players: Player[];
  shortlists: Record<string, string[]>;
  toggleShortlist: (teamId: string, playerId: string, user: AppUser) => Promise<void>;
}

const OwnerDashboard = ({ currentUser, teams, players, shortlists, toggleShortlist }: OwnerDashboardProps) => {
  const ownerTeam = teams.find(t => t.id === currentUser.teamId);
  const teamPlayers = players.filter(p => p.teamId === currentUser.teamId);
  const unsoldPlayers = players.filter(p => p.status === 'unsold');

  const shortlistStorageKey = `owner-shortlist-${currentUser.uid}-${currentUser.teamId || 'none'}`;
  const [shortlistedIds, setShortlistedIds] = useState<string[]>([]);
  const [showShortlistedOnly, setShowShortlistedOnly] = useState(false);

  // Filters state
  const [searchUnsold, setSearchUnsold] = useState('');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [sideFilter, setSideFilter] = useState<SideFilter>('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPlayer, setLightboxPlayer] = useState<Player | null>(null);

  useEffect(() => {
    // Prefer Firestore shortlist; fall back to localStorage if none
    const remote = shortlists[currentUser.teamId || ''];
    if (remote !== undefined) {
      setShortlistedIds(remote);
    } else {
      try {
        const stored = localStorage.getItem(shortlistStorageKey);
        if (stored) setShortlistedIds(JSON.parse(stored));
      } catch {
        setShortlistedIds([]);
      }
    }
  }, [shortlists, currentUser.teamId, shortlistStorageKey]);

  // Calculate totals for stat cards
  const totalCounts = useMemo(() => {
    const male = unsoldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length;
    const female = unsoldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length;
    const right = unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length;
    const left = unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length;
    const both = unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length;
    return { total: unsoldPlayers.length, male, female, right, left, both };
  }, [unsoldPlayers]);

  // Dynamic counts respecting opposite filter
  const dynamicCounts = useMemo(() => {
    const sideFiltered = sideFilter === 'all'
      ? unsoldPlayers
      : unsoldPlayers.filter(p => p.details?.playing_side?.toLowerCase() === sideFilter);

    const genderFiltered = genderFilter === 'all'
      ? unsoldPlayers
      : unsoldPlayers.filter(p => p.details?.player_gender?.toLowerCase() === genderFilter);

    return {
      total: unsoldPlayers.length,
      male: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length,
      female: sideFiltered.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length,
      right: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'right').length,
      left: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'left').length,
      both: genderFiltered.filter(p => p.details?.playing_side?.toLowerCase() === 'both').length,
    };
  }, [unsoldPlayers, genderFilter, sideFilter]);

  const resetFilters = () => {
    setGenderFilter('all');
    setSideFilter('all');
    setSearchUnsold('');
    setShowShortlistedOnly(false);
  };

  const isFiltersActive = genderFilter === 'all' && sideFilter === 'all' && !showShortlistedOnly;

  const exportMyPlayers = () => {
    const headers = [
      { key: 'name', label: 'Player Name' },
      { key: 'mobile_number', label: 'Mobile' },
      { key: 'gender', label: 'Gender' },
      { key: 'side', label: 'Playing Side' },
      { key: 'jersey_name', label: 'Jersey Name' },
      { key: 'jersey_number', label: 'Jersey Number' },
      { key: 'sold_price', label: 'Sold Price' },
    ];

    const rows = teamPlayers.map(p => ({
      name: p.name,
      mobile_number: p.mobile_number,
      gender: p.details?.player_gender || '',
      side: p.details?.playing_side || '',
      jersey_name: p.details?.jersey_name || '',
      jersey_number: p.details?.jersey_number || '',
      sold_price: p.soldPrice || 0,
    }));

    const csvHeader = headers.map(h => `"${h.label}"`).join(',');
    const csvRows = rows.map(row =>
      headers.map(h => {
        const val = (row as Record<string, unknown>)[h.key];
        if (val === null || val === undefined) return '""';
        const str = String(val);
        return `"${str.replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${ownerTeam?.name || 'team'}-players.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredUnsold = useMemo(() => {
    return unsoldPlayers.filter(p => {
      if (showShortlistedOnly && !shortlistedIds.includes(p.id)) return false;

      // Gender filter
      if (genderFilter !== 'all') {
        const gender = p.details?.player_gender?.toLowerCase();
        if (gender !== genderFilter) return false;
      }
      // Side filter
      if (sideFilter !== 'all') {
        const side = p.details?.playing_side?.toLowerCase();
        if (side !== sideFilter) return false;
      }

      if (searchUnsold.trim()) {
        const q = searchUnsold.toLowerCase();
        return (
          p.name.toLowerCase().includes(q) ||
          p.mobile_number?.includes(q) ||
          p.details?.jersey_name?.toLowerCase().includes(q) ||
          p.details?.player_full_name?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [unsoldPlayers, showShortlistedOnly, shortlistedIds, searchUnsold, genderFilter, sideFilter]);

  // Paginate filtered players
  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUnsold.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUnsold, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [genderFilter, sideFilter, searchUnsold, showShortlistedOnly]);

  if (!ownerTeam) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <h3 className="text-lg font-bold text-amber-800">Team Not Configured</h3>
          <p className="text-amber-600 mt-2">Please contact the administrator to link your team.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{ownerTeam.name}</h2>
          <p className="text-slate-500 text-sm">Owned by {ownerTeam.ownerName}</p>
        </div>
        {teamPlayers.length > 0 && (
          <button
            onClick={exportMyPlayers}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            <Download className="w-4 h-4" /> Export Players
          </button>
        )}
      </div>

      <div className="mb-2">
        <WalletCard team={ownerTeam} />
      </div>

      <h3 className="text-lg font-bold text-slate-800">Your Squad ({teamPlayers.length} Players)</h3>
      
      {teamPlayers.length === 0 ? (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-slate-500">No players acquired yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {teamPlayers.map(player => (
            <PlayerCard
              key={player.id}
              player={player}
              teams={teams}
              currentUser={currentUser}
              onManagePlayer={() => {}}
              onPhotoClick={p => {
                setLightboxPlayer(p);
                setLightboxOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Unsold Players for Owner */}
      <div className="space-y-4 pt-8 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">Unsold Players</h3>
          {(!isFiltersActive || searchUnsold) && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-1 px-3 py-1 text-sm text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Reset All
            </button>
          )}
        </div>

        {/* Filter Cards - Gender */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FilterCard
            icon={<Users className="w-5 h-5" />}
            label="All Unsold"
            count={filteredUnsold.length}
            totalCount={totalCounts.total}
            active={isFiltersActive}
            onClick={resetFilters}
            color="indigo"
          />
          <FilterCard
            icon={<Users className="w-5 h-5" />}
            label="Male Players"
            count={dynamicCounts.male}
            totalCount={totalCounts.male}
            active={genderFilter === 'male'}
            onClick={() => setGenderFilter(genderFilter === 'male' ? 'all' : 'male')}
            color="amber"
          />
          <FilterCard
            icon={<Users className="w-5 h-5" />}
            label="Female Players"
            count={dynamicCounts.female}
            totalCount={totalCounts.female}
            active={genderFilter === 'female'}
            onClick={() => setGenderFilter(genderFilter === 'female' ? 'all' : 'female')}
            color="pink"
          />
        </div>

        {/* Filter Cards - Playing Side */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FilterCard
            icon={<ArrowRight className="w-5 h-5" />}
            label="Right Side"
            count={dynamicCounts.right}
            totalCount={totalCounts.right}
            active={sideFilter === 'right'}
            onClick={() => setSideFilter(sideFilter === 'right' ? 'all' : 'right')}
            color="blue"
          />
          <FilterCard
            icon={<ArrowLeft className="w-5 h-5" />}
            label="Left Side"
            count={dynamicCounts.left}
            totalCount={totalCounts.left}
            active={sideFilter === 'left'}
            onClick={() => setSideFilter(sideFilter === 'left' ? 'all' : 'left')}
            color="green"
          />
          <FilterCard
            icon={<ArrowLeftRight className="w-5 h-5" />}
            label="Both Sides"
            count={dynamicCounts.both}
            totalCount={totalCounts.both}
            active={sideFilter === 'both'}
            onClick={() => setSideFilter(sideFilter === 'both' ? 'all' : 'both')}
            color="purple"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-slate-500 text-sm">Showing {paginatedPlayers.length} of {filteredUnsold.length} matches</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={searchUnsold}
                  onChange={e => setSearchUnsold(e.target.value)}
                  placeholder="Search unsold players..."
                  className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={showShortlistedOnly}
                  onChange={e => setShowShortlistedOnly(e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                Show shortlisted
              </label>
            </div>
          </div>

          {filteredUnsold.length === 0 ? (
            <div className="text-center text-slate-500 py-6 border border-dashed border-slate-200 rounded-lg">
              No unsold players match your filters.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedPlayers.map(player => {
                  const isShortlisted = shortlistedIds.includes(player.id);
                  return (
                    <div key={player.id} className="relative group">
                      <PlayerCard
                        player={player}
                        teams={teams}
                        currentUser={currentUser}
                        onManagePlayer={() => {}}
                        onPhotoClick={p => {
                          setLightboxPlayer(p);
                          setLightboxOpen(true);
                        }}
                      />
                      <button
                        onClick={async () => {
                          await toggleShortlist(ownerTeam.id, player.id, currentUser);
                        }}
                        className={`absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold shadow-md transition-all transform active:scale-95 ${
                          isShortlisted
                            ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-300'
                            : 'bg-white text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <Star className={`w-3.5 h-3.5 ${isShortlisted ? 'fill-yellow-900 text-yellow-900' : 'text-slate-400'}`} />
                        {isShortlisted ? 'Shortlisted' : 'Shortlist'}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalItems={filteredUnsold.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </div>
      </div>

      <PhotoLightbox
        imageUrl={lightboxPlayer?.details?.photo_url || ''}
        playerName={lightboxPlayer?.name || ''}
        isOpen={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setLightboxPlayer(null);
        }}
      />
    </div>
  );
};

export default OwnerDashboard;
